import isEmpty from 'lodash/isEmpty';

declare global {
  interface Window {
    desktop?: {
      mode: string;
      openLinkInBrowser: (url: string) => void;
      triggerSystemNotification?: (options: Notification) => void;
      handleSystemNotificationAction?: (
        callback: (notification: Notification, action: number) => void
      ) => void;
      handleSystemNotificationClick?: (
        callback: (notification: Notification) => void
      ) => void;
      removeSystemNotificationListeners?: () => void;
      changeMode?: (mode: string, initialPath?: string) => void;
      recording?: {
        start: (meetingUuid?: string) => Promise<DesktopRecordingResult>;
        stop: () => Promise<DesktopRecordingResult>;
        status: () => Promise<DesktopRecordingStatus>;
      };
    };
  }
}

export interface DesktopRecordingResult {
  ok: boolean;
  sessionId?: string;
  localAudioPath?: string;
  durationMs?: number;
  bytes?: number;
  errorCode?: string;
  errorMessage?: string;
}

export interface DesktopRecordingStatus {
  state: string;
  sessionId?: string;
  localAudioPath?: string;
}

export interface Notification {
  id: string;
  title: string;
  subtitle?: string;
  body?: string;
  actions: Array<{ type: string; text: string }>;
}

export const desktopAppModes = {
  ZEN_MODE: 'zen',
  FULL_MODE: 'full'
};

export const isDesktopContext = () => !isEmpty(window.desktop);

export const isFullMode = (): boolean =>
  window.desktop?.mode === desktopAppModes.FULL_MODE;

export const isZenMode = (): boolean =>
  window.desktop?.mode === desktopAppModes.ZEN_MODE;

export const canRecordFromDesktop = (): boolean =>
  typeof window.desktop?.recording?.start === 'function';

/**
 * Invokes the desktop recorder over IPC. Passing `meetingUuid` attaches the
 * capture to an existing meeting (the row case); omitting it lets the desktop
 * app create a fresh meeting shell (the impromptu / top-record case).
 * Returns a structured result even when not running inside the desktop app, so
 * callers can surface a friendly message instead of throwing.
 */
export const startDesktopRecording = async (
  meetingUuid?: string
): Promise<DesktopRecordingResult> => {
  if (!canRecordFromDesktop()) {
    return {
      ok: false,
      errorCode: 'NOT_DESKTOP',
      errorMessage:
        'Desktop recording is only available in the Avoma desktop app.'
    };
  }
  return window.desktop!.recording!.start(meetingUuid);
};
